import { useState, useEffect } from 'react';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Input } from '~/shared/shadcn/ui/input';
import { Label } from '~/shared/shadcn/ui/label';
import { ScrollArea } from '~/shared/shadcn/ui/scroll-area';
import { Checkbox } from '~/shared/shadcn/ui/checkbox';
import { Badge } from '~/shared/shadcn/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import { Calendar, Clock, MapPin, Users, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useGetAllActs } from '../api/get-all-acts';
import type { Pagination } from '~/entities/classes/api/get-all-classes-with-search';

interface Class {
  _id: string;
  name: string;
  location?: string;
  dayOfWeek?: string;
  startTime?: string;
  instructor?: string;
  students: Student[];
}

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
}

interface ClassImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  recitalId: string;
  showId: string;
  classes: Class[];
  isLoadingClasses?: boolean;
  onImport: (classIds: string[], showId: string, defaultDuration?: number, actId?: string, selectedStudentIds?: string[]) => Promise<void>;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  pagination?: Pagination;
  onLoadMore?: () => void;
}

export function ClassImportDialog({
  isOpen,
  onClose,
  recitalId: _recitalId,
  showId,
  classes,
  isLoadingClasses = false,
  onImport,
  searchQuery = '',
  onSearchChange,
  pagination,
  onLoadMore,
}: ClassImportDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedStudentIdsSet, setSelectedStudentIdsSet] = useState<Set<string>>(new Set());
  const [selectedActId, setSelectedActId] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);

  // Fetch acts for the show
  const { data: actsData, isLoading: isLoadingActs } = useGetAllActs({ showId });
  const acts = actsData || [];

  useEffect(() => {
    if (!isOpen) {
      // Reset state when dialog closes
      setStep(1);
      setSelectedClass(null);
setSelectedStudentIdsSet(new Set());
      setSelectedActId('');
    }
  }, [isOpen]);

  // Auto-select first act when acts load
  useEffect(() => {
    if (acts.length > 0 && !selectedActId && acts[0]) {
      setSelectedActId(acts[0]._id);
    }
  }, [acts, selectedActId]);

  const handleSelectClass = (classData: Class) => {
    setSelectedClass(classData);
    // Auto-select all students from the class
    const studentIds = new Set(classData.students.map((s) => s._id));
    setSelectedStudentIdsSet(studentIds);
    setStep(2);
  };

  const handleToggleStudent = (studentId: string) => {
    const newSelection = new Set(selectedStudentIdsSet);
    if (newSelection.has(studentId)) {
      newSelection.delete(studentId);
    } else {
      newSelection.add(studentId);
    }
    setSelectedStudentIdsSet(newSelection);
  };

const handleImport = async () => {
    if (!selectedClass || selectedStudentIdsSet.size === 0) return;

    if (!selectedActId) {
      toast.error('Please select an act');
      return;
    }

    setIsImporting(true);
    try {
      const selectedStudentIds = Array.from(selectedStudentIdsSet);
      await onImport([selectedClass._id], showId, 5, selectedActId, selectedStudentIds); // Pass actId and selected student IDs
      
      toast.success('Class imported successfully as performance');
      
      handleClose();
    } catch (error) {
      console.error('Error importing class:', error);
      toast.error('Failed to import class');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedClass(null);
    setSelectedStudentIdsSet(new Set());
    onClose();
  };

  const groupedClasses = classes.reduce(
    (acc, cls) => {
      const location = cls.location || 'No Location';
      if (!acc[location]) acc[location] = {};

      const day = cls.dayOfWeek || 'No Day';
      if (!acc[location][day]) acc[location][day] = [];

      acc[location][day].push(cls);
      return acc;
    },
    {} as Record<string, Record<string, Class[]>>,
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {step === 1 ? 'Select Class' : 'Select Students'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Choose a class to import as a performance'
              : 'Adjust which students will perform in this routine'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <>
            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search classes by name..."
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[500px] pr-4">
              {isLoadingClasses ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Loading classes...</p>
                </div>
              ) : Object.keys(groupedClasses).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-muted-foreground mb-2">
                    {searchQuery ? `No classes found matching "${searchQuery}"` : 'No classes found'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ? 'Try a different search term' : 'Create classes first to use this feature'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    {Object.entries(groupedClasses).map(([location, dayGroups]) => (
                      <div key={location}>
                        <div className="mb-3 flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <h3 className="font-semibold text-lg">{location}</h3>
                        </div>
                        <div className="space-y-4 ml-6">
                          {Object.entries(dayGroups).map(([day, classList]) => (
                            <div key={day}>
                              <div className="mb-2 flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                <h4 className="font-medium text-sm text-muted-foreground">{day}</h4>
                              </div>
                              <div className="space-y-2 ml-5">
                                {classList.map((cls) => (
                                  <button
                                    key={cls._id}
                                    onClick={() => handleSelectClass(cls)}
                                    className="w-full rounded-lg border bg-card p-4 text-left transition-all hover:border-primary hover:shadow-md"
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <h5 className="font-semibold">{cls.name}</h5>
                                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                          {cls.startTime && (
                                            <span className="flex items-center gap-1">
                                              <Clock className="h-3 w-3" />
                                              {cls.startTime}
                                            </span>
                                          )}
                                          {cls.instructor && (
                                            <span className="flex items-center gap-1">
                                              <Users className="h-3 w-3" />
                                              {cls.instructor}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="secondary">
                                          {cls.students.length} students
                                        </Badge>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {pagination && pagination.page < pagination.totalPages && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        onClick={onLoadMore}
                        disabled={isLoadingClasses}
                      >
                        {isLoadingClasses ? 'Loading...' : `Load More (${pagination.totalCount - (pagination.page * pagination.limit)} remaining)`}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </ScrollArea>
          </>
        )}

        {step === 2 && selectedClass && (
          <>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{selectedClass.name}</h4>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {selectedClass.dayOfWeek && <span>{selectedClass.dayOfWeek}</span>}
                    {selectedClass.startTime && <span>• {selectedClass.startTime}</span>}
                    {selectedClass.location && <span>• {selectedClass.location}</span>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                  Change Class
                </Button>
              </div>
            </div>

            {/* Act Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Select Act <span className="text-red-500">*</span>
              </Label>
              {isLoadingActs ? (
                <div className="text-sm text-muted-foreground">Loading acts...</div>
              ) : acts.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    No acts found for this show
                  </p>
                  <p className="text-xs text-muted-foreground">
                    A default act will be created automatically
                  </p>
                </div>
              ) : (
                <Select value={selectedActId} onValueChange={setSelectedActId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an act for this performance" />
                  </SelectTrigger>
                  <SelectContent>
                    {acts.map((act, index) => (
                      <SelectItem key={act._id} value={act._id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            Act {index + 1}: {act.name}
                          </span>
                          {act.duration && (
                            <span className="text-xs text-muted-foreground">
                              ({act.duration} min)
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">
                    Students ({selectedStudentIdsSet.size} selected)
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedStudentIdsSet.size === selectedClass.students.length) {
                        // Deselect all
                        setSelectedStudentIdsSet(new Set());
                      } else {
                        // Select all
                        const allStudentIds = new Set(selectedClass.students.map(s => s._id));
                        setSelectedStudentIdsSet(allStudentIds);
                      }
                    }}
                  >
                    {selectedStudentIdsSet.size === selectedClass.students.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>
                {selectedClass.students.map((student) => (
                  <div
                    key={student._id}
                    className="flex items-center gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-accent"
                  >
                    <Checkbox
                      checked={selectedStudentIdsSet.has(student._id)}
                      onCheckedChange={() => handleToggleStudent(student._id)}
                      id={`student-${student._id}`}
                    />
                    <label
                      htmlFor={`student-${student._id}`}
                      className="flex-1 cursor-pointer text-sm font-medium"
                    >
                      {student.firstName} {student.lastName}
                    </label>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={isImporting || selectedStudentIdsSet.size === 0}
                className="flex-1"
              >
                {isImporting
                  ? 'Importing...'
                  : `Import Performance (${selectedStudentIdsSet.size} students)`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
